import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 italic mb-8">Last updated: October 2025</p>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Expected Value Club ("EVC," "we," "our," or "us") respects your privacy and is committed to protecting your personal information.<br />
              This Privacy Policy explains how we collect, use, and safeguard the information you provide when using our website (the "Site") and related services.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              By accessing or using the Site, you consent to this Privacy Policy.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">1. Information We Collect</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">We collect information in the following ways:</p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">a. Information You Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you register, apply for membership, submit ideas, or communicate with us, you may provide:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Name, email address, and login credentials</li>
              <li>Profile details or application information</li>
              <li>Content submissions (e.g., investment ideas, comments, or messages)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">b. Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you use our Site, we may automatically collect:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Log data (IP address, browser type, operating system, referring URL, timestamps)</li>
              <li>Cookies and similar technologies to enhance site performance and user experience</li>
              <li>Usage analytics (e.g., page visits, engagement, time on site)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">c. Third-Party Data</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you log in using third-party services (e.g., Google, X/Twitter), those platforms may provide limited profile information in accordance with their privacy policies.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">2. How We Use Your Information</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Operate, maintain, and improve the Site</li>
              <li>Evaluate membership applications</li>
              <li>Display and attribute user-submitted ideas and rankings</li>
              <li>Communicate with members about updates, new features, or moderation</li>
              <li>Monitor for fraud, misuse, or security issues</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              We do <strong>not</strong> sell or rent your personal information to third parties.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">3. Cookies and Tracking</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Keep you signed in</li>
              <li>Measure traffic and engagement</li>
              <li>Improve the site's layout and usability</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              You can control or disable cookies through your browser settings, but some features of the Site may not function properly if you do.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">4. Data Sharing and Disclosure</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">We may share limited information:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>With service providers</strong> who help us operate the Site (e.g., hosting, analytics, security).</li>
              <li><strong>For legal compliance</strong>, if required by law, subpoena, or court order.</li>
              <li><strong>During business transitions</strong>, such as a merger, acquisition, or asset sale, subject to appropriate confidentiality obligations.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              We do <strong>not</strong> share member data for advertising or marketing resale purposes.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">5. Data Retention</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information only as long as necessary to fulfill the purposes described above, comply with our legal obligations, or resolve disputes.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              If you delete your account or request removal, we will make reasonable efforts to delete or anonymize your personal data, except where retention is required by law.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">6. Your Rights and Choices</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Access or request a copy of your data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for certain uses</li>
              <li>Object to or restrict data processing</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise any of these rights, contact us at <strong><a href="mailto:privacy@expectedvalueclub.com" className="text-primary-600 hover:text-primary-700">privacy@expectedvalueclub.com</a></strong>.<br />
              We will respond to verified requests within a reasonable timeframe.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">7. Security</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              We take reasonable administrative, technical, and physical measures to safeguard your information against unauthorized access, disclosure, alteration, or destruction.<br />
              However, <strong>no system is 100% secure</strong>, and we cannot guarantee the absolute security of your data.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">8. International Data Transfers</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              If you access the Site from outside the United States, you consent to the transfer and processing of your information in the U.S., where data protection laws may differ from those of your home country.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">9. Children's Privacy</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              The Site is not intended for individuals under 18 years of age.<br />
              We do not knowingly collect personal information from minors. If you believe a minor has provided us data, please contact us to have it deleted.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">10. Updates to This Policy</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal obligations.<br />
              Any revisions will be posted here with a new "Last Updated" date. Continued use of the Site constitutes acceptance of the updated policy.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">11. Contact Us</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions or concerns about this Privacy Policy, please contact:
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              📧 <strong><a href="mailto:privacy@expectedvalueclub.com" className="text-primary-600 hover:text-primary-700">privacy@expectedvalueclub.com</a></strong>
            </p>

            <hr className="my-8 border-gray-200" />

            <p className="text-center text-gray-600 mt-10 mb-6">
              © 2025 <strong>ExpectedValueClub.com</strong> — All rights reserved.
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;

